import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ThemePreset {
  name: string;
  label: string;
  primary: string;
  accent: string;
}

export interface ThemeConfig {
  primaryColor: string;
  accentColor: string;
  buttonColor: string;
  darkMode: boolean;
  sidebarLogoUrl: string;
  navbarLogoUrl: string;
  /** @deprecated use sidebarLogoUrl — kept for migration only */
  logoUrl?: string;
  appName: string;
}

const THEME_STORAGE_KEY = 'crm_white_label_theme_v2';

export const THEME_PRESETS: ThemePreset[] = [
  { name: 'brand', label: 'Brand Blue', primary: '#0243A3', accent: '#38BDF8' },
  { name: 'ocean', label: 'Ocean', primary: '#0d4259', accent: '#f0950c' },
  { name: 'forest', label: 'Forest', primary: '#1a5c38', accent: '#f5c518' },
  { name: 'violet', label: 'Violet', primary: '#4a1e8c', accent: '#ff6b35' },
  { name: 'crimson', label: 'Crimson', primary: '#8b1a1a', accent: '#ffa500' },
  { name: 'slate', label: 'Slate', primary: '#1e3a5f', accent: '#00bcd4' },
  { name: 'rose', label: 'Rose', primary: '#7b2d5c', accent: '#ffc107' },
  { name: 'teal', label: 'Teal', primary: '#00695c', accent: '#ff7043' },
  { name: 'indigo', label: 'Indigo', primary: '#283593', accent: '#ff9800' },
  {
    name: 'midnight',
    label: 'Midnight',
    primary: '#1a1a2e',
    accent: '#e94560',
  },
  { name: 'emerald', label: 'Emerald', primary: '#065f46', accent: '#fbbf24' },
  { name: 'copper', label: 'Copper', primary: '#7c3d12', accent: '#34d399' },
  { name: 'navy', label: 'Navy', primary: '#0f172a', accent: '#38bdf8' },
  { name: 'plum', label: 'Plum', primary: '#581c87', accent: '#f9a8d4' },
  { name: 'pine', label: 'Pine', primary: '#14532d', accent: '#fb923c' },
  { name: 'steel', label: 'Steel', primary: '#374151', accent: '#60a5fa' },
  {
    name: 'burgundy',
    label: 'Burgundy',
    primary: '#6b1022',
    accent: '#a3e635',
  },
];

const DEFAULT_THEME: ThemeConfig = {
  primaryColor: '#0243A3',
  accentColor: '#38BDF8',
  buttonColor: '#0243A3',
  darkMode: false,
  sidebarLogoUrl: '',
  navbarLogoUrl: '',
  appName: 'CRM',
};

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

function adjustColor(hex: string, amount: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = Math.max(0, Math.min(255, parseInt(result[1], 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(result[2], 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(result[3], 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function getContrastText(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '#ffffff';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  // Luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private _config = new BehaviorSubject<ThemeConfig>(DEFAULT_THEME);
  config$ = this._config.asObservable();

  get currentConfig(): ThemeConfig {
    return this._config.getValue();
  }

  /** Call once on app startup */
  init(): void {
    const saved = this.loadFromStorage();
    this.applyTheme(saved);
  }

  applyTheme(config: ThemeConfig): void {
    this._config.next(config);
    this.saveToStorage(config);
    this.applyVariables(config);
    this.applyDarkMode(config.darkMode);
  }

  updatePrimary(color: string): void {
    this.applyTheme({ ...this.currentConfig, primaryColor: color });
  }

  updateAccent(color: string): void {
    this.applyTheme({ ...this.currentConfig, accentColor: color });
  }

  updateButtonColor(color: string): void {
    this.applyTheme({ ...this.currentConfig, buttonColor: color });
  }

  toggleDarkMode(enabled: boolean): void {
    this.applyTheme({ ...this.currentConfig, darkMode: enabled });
  }

  updateSidebarLogo(url: string): void {
    this.applyTheme({ ...this.currentConfig, sidebarLogoUrl: url });
  }

  updateNavbarLogo(url: string): void {
    this.applyTheme({ ...this.currentConfig, navbarLogoUrl: url });
  }

  updateAppName(name: string): void {
    this.applyTheme({ ...this.currentConfig, appName: name });
  }

  applyPreset(preset: ThemePreset): void {
    this.applyTheme({
      ...this.currentConfig,
      primaryColor: preset.primary,
      accentColor: preset.accent,
    });
  }

  resetToDefault(): void {
    this.applyTheme({ ...DEFAULT_THEME });
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private applyVariables(config: ThemeConfig): void {
    const root = document.documentElement;
    const p = config.primaryColor;
    const a = config.accentColor;

    root.style.setProperty('--primary', p);
    root.style.setProperty('--primary-rgb', hexToRgb(p));
    root.style.setProperty('--primary-dark', adjustColor(p, -5));
    root.style.setProperty('--primary-light', adjustColor(p, 30));
    root.style.setProperty('--primary-text', getContrastText(p));

    root.style.setProperty('--accent', a);
    root.style.setProperty('--accent-rgb', hexToRgb(a));
    root.style.setProperty('--accent-dark', adjustColor(a, -5));
    root.style.setProperty('--accent-text', getContrastText(a));

    const b = config.buttonColor || p;
    root.style.setProperty('--button', b);
    root.style.setProperty('--button-rgb', hexToRgb(b));
    root.style.setProperty('--button-dark', adjustColor(b, -20));
    root.style.setProperty('--button-text', getContrastText(b));

    // Legacy aliases so existing code using --bg and --orange keeps working
    root.style.setProperty('--bg', p);
    root.style.setProperty('--orange', a);
  }

  private applyDarkMode(enabled: boolean): void {
    if (enabled) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }

  private saveToStorage(config: ThemeConfig): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(config));
    } catch {
      /* ignore quota errors */
    }
  }

  private loadFromStorage(): ThemeConfig {
    try {
      const raw = localStorage.getItem(THEME_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Migrate old single logoUrl to sidebarLogoUrl
        if (parsed.logoUrl && !parsed.sidebarLogoUrl) {
          parsed.sidebarLogoUrl = parsed.logoUrl;
        }
        delete parsed.logoUrl;
        return { ...DEFAULT_THEME, ...parsed };
      }
    } catch {
      /* ignore */
    }
    return { ...DEFAULT_THEME };
  }
}
